import React, { useRef, useEffect } from "react";

/**
 * Interactive "constellation" background.
 * Floating dots connected by lines that react and drift toward the cursor.
 * Pure canvas, no dependencies. Respects prefers-reduced-motion.
 */
export default function ParticleNet() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let animationId;

    // Cursor influence point (starts off-screen).
    const mouse = { x: -9999, y: -9999, active: false };

    const COLORS = ["#22d3ee", "#a855f7", "#38bdf8"]; // cyan / purple / sky

    function particleCount() {
      // Scale density with viewport but keep it sane on phones.
      const area = width * height;
      return Math.min(Math.max(Math.round(area / 14000), 40), 140);
    }

    function makeParticle() {
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 1.8 + 0.8,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      };
    }

    function init() {
      particles = [];
      const count = particleCount();
      for (let i = 0; i < count; i++) particles.push(makeParticle());
    }

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      init();
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);

      const linkDist = 130;
      const mouseDist = 180;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Drift.
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges.
        if (p.x < -50) p.x = width + 50;
        if (p.x > width + 50) p.x = -50;
        if (p.y < -50) p.y = height + 50;
        if (p.y > height + 50) p.y = -50;

        // Gentle pull toward cursor.
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < mouseDist && dist > 0.1) {
            const force = (1 - dist / mouseDist) * 0.6;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;
          }
        }

        // Dot.
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.9;
        ctx.fill();

        // Links to nearby particles.
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dist = Math.hypot(dx, dy);
          if (dist < linkDist) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (1 - dist / linkDist) * 0.25;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }

        // Bright links to cursor.
        if (mouse.active) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < mouseDist) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = "#67e8f9";
            ctx.globalAlpha = (1 - dist / mouseDist) * 0.5;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;
      animationId = requestAnimationFrame(draw);
    }

    function onMove(e) {
      const point = e.touches ? e.touches[0] : e;
      mouse.x = point.clientX;
      mouse.y = point.clientY;
      mouse.active = true;
    }

    function onLeave() {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    }

    resize();

    if (prefersReduced) {
      // Draw a single static frame, no animation loop.
      draw();
      cancelAnimationFrame(animationId);
    } else {
      draw();
      window.addEventListener("mousemove", onMove);
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("mouseout", onLeave);
    }

    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="particle-net" aria-hidden="true" />;
}
